/**
 * =============================================================================
 * Snowcubes Squiggle Frame System - Brand Primitive
 * 
 * Generates hand-drawn border SVGs for any element with .sc-squiggle-frame--full
 * 
 * Usage:
 *   <div class="my-section sc-squiggle-frame--full">Content</div>
 * 
 * See: documents/HAND_DRAWN_BORDER_GUIDE.md
 * =============================================================================
 */

(function() {
  'use strict';

  const SQUIGGLE_DEFAULTS = {
    strokeWidth: 2,
    minAmplitude: 0.8,
    maxAmplitude: 6,
    minCornerRadius: 4,
    maxCornerRadius: 12,
    smoothingPasses: 3,
    stepScale: 0.35
  };

  // -------------------------------------------------------------------------
  // Utility Functions
  // -------------------------------------------------------------------------

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  function parsePixelValue(value, fallback = 0) {
    if (typeof value !== 'string') return fallback;
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : fallback;
  }

  function createRng(seed) {
    let t = seed >>> 0;
    return function random() {
      t += 0x6d2b79f5;
      let r = Math.imul(t ^ (t >>> 15), 1 | t);
      r ^= r + Math.imul(r ^ (r >>> 7), 61 | r);
      return ((r ^ (r >>> 14)) >>> 0) / 4294967296;
    };
  }

  /**
   * Simple string hash function (djb2 variant)
   * Creates a stable numeric hash from any string
   */
  function hashString(str) {
    let hash = 5381;
    for (let i = 0; i < str.length; i++) {
      hash = ((hash << 5) + hash) ^ str.charCodeAt(i);
    }
    return hash >>> 0;
  }

  /**
   * Deterministic per-seed style profile.
   * Preserves the Snowcubes look while increasing per-frame uniqueness.
   */
  function getSeededStyleProfile(seed) {
    const rng = createRng((seed ^ 0x9e3779b9) >>> 0);
    const maxAmplitude = clamp(
      SQUIGGLE_DEFAULTS.maxAmplitude * (0.82 + rng() * 0.5),
      4.8,
      8.5
    );

    return {
      amplitudeScale: 0.78 + rng() * 0.52,
      cornerScale: 0.75 + rng() * 0.5,
      segmentScale: 0.8 + rng() * 0.45,
      maxAmplitude,
      stepScale: SQUIGGLE_DEFAULTS.stepScale * (0.75 + rng() * 0.55),
      smoothingPasses:
        rng() < 0.35
          ? Math.max(1, SQUIGGLE_DEFAULTS.smoothingPasses - 1)
          : SQUIGGLE_DEFAULTS.smoothingPasses
    };
  }

  /**
   * Per-edge "handwriting" profile.
   * This changes the character of each side (not just tiny noise),
   * while staying subtle enough to avoid jarring borders.
   */
  function getSeededEdgeStyles(seed) {
    const rng = createRng((seed ^ 0xa511e9b3) >>> 0);
    const archetypes = [
      { waveBase: 0.06, bowBase: 0.12, taperBase: 1.12 },
      { waveBase: 0.14, bowBase: 0.05, taperBase: 0.88 },
      { waveBase: 0.05, bowBase: 0.15, taperBase: 1.18 },
      { waveBase: 0.12, bowBase: 0.08, taperBase: 0.92 },
      { waveBase: 0.09, bowBase: 0.11, taperBase: 1.06 }
    ];
    const archetype = archetypes[Math.floor(rng() * archetypes.length)] || archetypes[0];

    const makeEdge = () => ({
      amplitudeScale: 0.7 + rng() * 0.6,
      segmentScale: 0.72 + rng() * 0.56,
      stepScale: 0.7 + rng() * 0.6,
      taperPower: clamp(archetype.taperBase + (rng() - 0.5) * 0.6, 0.65, 1.45),
      waveStrength: clamp(archetype.waveBase + (rng() - 0.5) * 0.16, 0.02, 0.22),
      waveCycles: rng() < 0.55 ? 1 : 2,
      wavePhase: rng() * Math.PI * 2,
      bowStrength: clamp(archetype.bowBase + (rng() - 0.5) * 0.16, 0.02, 0.24),
      asymmetry: (rng() - 0.5) * 0.4,
      smoothingDelta: rng() < 0.45 ? -1 : 0
    });

    return {
      top: makeEdge(),
      right: makeEdge(),
      bottom: makeEdge(),
      left: makeEdge()
    };
  }

  let requestSeedCounter = 0;

  function normalizeSeedMode(value) {
    return String(value || '').trim().toLowerCase();
  }

  function isRequestSeedModeValue(value) {
    return value === 'request' || value === 'per-request' || value === 'random';
  }

  function isStableSeedModeValue(value) {
    return value === 'stable' || value === 'deterministic' || value === 'fixed';
  }

  function isRequestSeedMode(element) {
    const elementMode = normalizeSeedMode(element?.dataset?.squiggleSeedMode);
    if (isRequestSeedModeValue(elementMode)) return true;
    if (isStableSeedModeValue(elementMode)) return false;

    if (typeof document !== 'undefined') {
      const rootMode = normalizeSeedMode(document.documentElement?.dataset?.squiggleSeedMode);
      if (isRequestSeedModeValue(rootMode)) return true;
      if (isStableSeedModeValue(rootMode)) return false;

      const bodyMode = normalizeSeedMode(document.body?.dataset?.squiggleSeedMode);
      if (isRequestSeedModeValue(bodyMode)) return true;
      if (isStableSeedModeValue(bodyMode)) return false;
    }

    if (typeof window !== 'undefined') {
      const globalMode = normalizeSeedMode(window.SNOWCUBES_SQUIGGLE_SEED_MODE);
      if (isRequestSeedModeValue(globalMode)) return true;
      if (isStableSeedModeValue(globalMode)) return false;
    }

    // Default to per-request variation so repeated gallery cards don't share
    // server-provided deterministic seeds unless explicitly forced to stable mode.
    return true;
  }

  function generateRequestSeed() {
    requestSeedCounter += 1;
    const now = Date.now();
    const perfNow =
      typeof performance !== 'undefined' && typeof performance.now === 'function'
        ? performance.now()
        : 0;
    const entropy = `${now}:${perfNow}:${Math.random()}:${requestSeedCounter}`;
    return hashString(entropy);
  }

  // -------------------------------------------------------------------------
  // Seed Generation
  // -------------------------------------------------------------------------

  /**
   * Get or generate a stable seed for squiggle generation.
   * Priority:
   *   1. Per-request seed (default; stable per element during the page session)
   *   2. Explicit `data-squiggle-seed` / content/position hash (stable mode only)
   *   3. Random seed (fallback)
   * 
   * SSR-friendly: Set data-squiggle-seed in Liquid for deterministic output:
   *   <div class="sc-squiggle-frame--full" data-squiggle-seed="{{ article.id }}">
   */
  function getSquiggleSeed(element) {
    if (isRequestSeedMode(element)) {
      const existingRequestSeed = String(element.dataset.squiggleRequestSeed || '').trim();
      if (/^\d+$/.test(existingRequestSeed)) {
        const asNum = Number(existingRequestSeed);
        if (Number.isFinite(asNum) && asNum > 0) {
          return asNum;
        }
      }

      const requestSeed = generateRequestSeed();
      element.dataset.squiggleRequestSeed = String(requestSeed);
      return requestSeed;
    }

    // Priority 1: Explicit seed attribute (server-side or manual)
    const existing = element.dataset.squiggleSeed;
    if (existing) {
      const trimmed = String(existing).trim();
      // Only accept pure integer seeds to avoid parseInt("123abc") collisions.
      if (/^\d+$/.test(trimmed)) {
        const asNum = Number(trimmed);
        if (Number.isFinite(asNum) && asNum > 0) {
          return asNum;
        }
      }
      // Otherwise hash the string
      const hashed = hashString(trimmed);
      element.dataset.squiggleSeed = String(hashed);
      return hashed;
    }

    // Priority 2: Content-based hash for stability
    const stableIdentifier = element.id || 
      element.dataset.draftSlug ||
      element.querySelector('h1,h2,h3')?.textContent ||
      element.textContent?.slice(0, 100);
    
    if (stableIdentifier && stableIdentifier.trim().length > 5) {
      const contentHash = hashString(stableIdentifier);
      element.dataset.squiggleSeed = String(contentHash);
      return contentHash;
    }

    // Priority 3: Position-based seed for galleries/lists (each item gets unique squiggle)
    // Combines parent context with sibling index for stable, unique seeds per element
    const parent = element.parentElement;
    const siblingIndex = parent ? Array.from(parent.children).indexOf(element) : 0;
    const parentId = parent?.id || parent?.className?.split(' ')[0] || 'root';
    const img = element.querySelector('img');
    const imgSrc = img?.src || img?.dataset?.src || '';
    // Include sibling index prominently to ensure different seeds
    const positionKey = `${parentId}__idx${siblingIndex}__${imgSrc.slice(-30)}`;
    
    if (positionKey.length > 10) {
      // Multiply by prime based on index to ensure different patterns
      const baseHash = hashString(positionKey);
      const indexMultiplier = (siblingIndex + 1) * 7919; // Large prime
      const positionHash = (baseHash ^ indexMultiplier) >>> 0;
      element.dataset.squiggleSeed = String(positionHash);
      return positionHash;
    }

    // Priority 4: Random seed (varies per session)
    const seed = Math.floor(Math.random() * 1e9);
    element.dataset.squiggleSeed = String(seed);
    return seed;
  }

  // -------------------------------------------------------------------------
  // Path Generation
  // -------------------------------------------------------------------------

  function buildEdgePoints(options) {
    const {
      start,
      end,
      normal,
      segments,
      amplitude,
      seed,
      includeFirst,
      stepScale,
      smoothingPasses,
      taperPower,
      waveStrength,
      waveCycles,
      wavePhase,
      bowStrength,
      asymmetry
    } = options;
    const points = [];
    const rng = createRng(seed);
    const count = Math.max(4, segments + 1);
    const tPositions = Array.from({ length: count }, (_, i) => i / (count - 1));

    // Subtle position jitter
    for (let i = 1; i < count - 1; i += 1) {
      const jitter = (rng() - 0.5) * (0.3 / segments);
      tPositions[i] = clamp(tPositions[i] + jitter, 0, 1);
    }
    tPositions.sort((a, b) => a - b);
    tPositions[0] = 0;
    tPositions[count - 1] = 1;

    // Random walk with gentle step
    const offsets = new Array(count);
    offsets[0] = 0;
    offsets[count - 1] = 0;
    const stepFactor = Number.isFinite(stepScale) ? stepScale : SQUIGGLE_DEFAULTS.stepScale;
    const step = amplitude * stepFactor;
    for (let i = 1; i < count - 1; i += 1) {
      offsets[i] = offsets[i - 1] + (rng() - 0.5) * step;
    }

    // Center the offsets
    const mean = offsets.reduce((sum, value) => sum + value, 0) / count;
    for (let i = 0; i < count; i += 1) {
      offsets[i] -= mean * 0.5;
    }

    // Multiple smoothing passes
    const passCount = Number.isFinite(smoothingPasses)
      ? Math.max(1, Math.round(smoothingPasses))
      : SQUIGGLE_DEFAULTS.smoothingPasses;
    for (let pass = 0; pass < passCount; pass += 1) {
      for (let i = 1; i < count - 1; i += 1) {
        offsets[i] = (offsets[i - 1] + offsets[i] * 2 + offsets[i + 1]) / 4;
      }
    }

    // Build points with taper
    for (let i = 0; i < count; i += 1) {
      if (!includeFirst && i === 0) continue;
      const t = tPositions[i];
      const taperBase = Math.sin(Math.PI * t);
      const taper = Math.pow(Math.max(0, taperBase), Number.isFinite(taperPower) ? taperPower : 1);
      const cycles = Number.isFinite(waveCycles) ? Math.max(1, Math.round(waveCycles)) : 1;
      const phase = Number.isFinite(wavePhase) ? wavePhase : 0;
      const edgeWave =
        Math.sin(t * Math.PI * 2 * cycles + phase) * amplitude * (Number.isFinite(waveStrength) ? waveStrength : 0);
      const edgeBow =
        (2 * t - 1) *
        (1 - Math.abs(2 * t - 1)) *
        amplitude *
        (Number.isFinite(bowStrength) ? bowStrength : 0);
      const sideAsymmetry = (2 * t - 1) * amplitude * (Number.isFinite(asymmetry) ? asymmetry : 0);
      const styleOffset = (edgeWave + edgeBow + sideAsymmetry) * taperBase;
      const offset = clamp(offsets[i] * taper + styleOffset, -amplitude, amplitude);
      const x = start.x + (end.x - start.x) * t + normal.x * offset;
      const y = start.y + (end.y - start.y) * t + normal.y * offset;
      points.push({ x, y });
    }

    return points;
  }

  function buildClosedPath(points) {
    if (!points || points.length < 4) return '';

    const round = (value) => Math.round(value * 100) / 100;
    let path = `M ${round(points[0].x)} ${round(points[0].y)}`;
    const total = points.length;

    for (let i = 0; i < total; i += 1) {
      const p0 = points[(i - 1 + total) % total];
      const p1 = points[i];
      const p2 = points[(i + 1) % total];
      const p3 = points[(i + 2) % total];

      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;

      path += ` C ${round(cp1x)} ${round(cp1y)}, ${round(cp2x)} ${round(cp2y)}, ${round(p2.x)} ${round(p2.y)}`;
    }

    return `${path} Z`;
  }

  function getPathBounds(pathStr) {
    const nums = (pathStr.match(/-?[\d.]+/g) || []).map((n) => parseFloat(n));
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < nums.length; i += 2) {
      const x = nums[i];
      const y = nums[i + 1];
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
    return { minX, minY, maxX, maxY };
  }

  function transformPathCoords(pathStr, transformX, transformY) {
    let idx = 0;
    const round = (value) => Math.round(value * 100) / 100;
    return pathStr.replace(/-?[\d.]+/g, function(m) {
      const v = parseFloat(m);
      const out = idx % 2 === 0 ? transformX(v) : transformY(v);
      idx++;
      return round(out).toString();
    });
  }

  /**
   * Ensure the final (smoothed) path fits inside its viewBox.
   *
   * Why: our Catmull-Rom -> Bezier control points can overshoot beyond the
   * generated points (especially near corners), which can:
   * - clip the SVG stroke at the viewBox edges
   * - cause "underflow" on --frame variants where the clip-path can't render
   *   outside the media box.
   *
   * We minimally scale + translate the path so its full coordinate bounds fit
   * within [pad, width-pad] / [pad, height-pad].
   */
	  function fitPathToViewBox(pathStr, width, height, strokeWidth, options) {
	    if (!pathStr) return '';

	    const bounds = getPathBounds(pathStr);
	    if (!Number.isFinite(bounds.minX) || !Number.isFinite(bounds.minY)) return pathStr;

	    // Default pad includes +1px safety to avoid faint stroke clipping at the viewBox
	    // edges. For cookie-cutter cutouts (inset=0), we want the stroke to reach the
	    // edge, so we skip the extra pixel.
	    const padExtra = options && options.padExtra !== undefined ? options.padExtra : 1;
	    const pad = Math.max(0, (Number.isFinite(strokeWidth) ? strokeWidth : 0) / 2 + padExtra);
	    const availW = Math.max(1, width - pad * 2);
	    const availH = Math.max(1, height - pad * 2);
	    const pathW = Math.max(0.0001, bounds.maxX - bounds.minX);
	    const pathH = Math.max(0.0001, bounds.maxY - bounds.minY);

	    const allowScaleUp = Boolean(options && options.allowScaleUp);
	    const scaleCap = allowScaleUp ? Infinity : 1;
	    const scale = Math.min(scaleCap, availW / pathW, availH / pathH);
    const cx = (bounds.minX + bounds.maxX) / 2;
    const cy = (bounds.minY + bounds.maxY) / 2;

    // Compute scaled bounds (still centered at cx/cy).
    const minX2 = cx + (bounds.minX - cx) * scale;
    const maxX2 = cx + (bounds.maxX - cx) * scale;
    const minY2 = cy + (bounds.minY - cy) * scale;
    const maxY2 = cy + (bounds.maxY - cy) * scale;

    // Minimal translation required to fit within padded viewBox.
    const minDx = pad - minX2;
    const maxDx = (width - pad) - maxX2;
    const minDy = pad - minY2;
    const maxDy = (height - pad) - maxY2;

    let dx = 0;
    if (minDx > maxDx) {
      dx = (minDx + maxDx) / 2;
    } else {
      if (dx < minDx) dx = minDx;
      if (dx > maxDx) dx = maxDx;
    }

    let dy = 0;
    if (minDy > maxDy) {
      dy = (minDy + maxDy) / 2;
    } else {
      if (dy < minDy) dy = minDy;
      if (dy > maxDy) dy = maxDy;
    }

	    const transformX = (x) => (x - cx) * scale + cx + dx;
	    const transformY = (y) => (y - cy) * scale + cy + dy;
	    return transformPathCoords(pathStr, transformX, transformY);
	  }

	  function buildSquigglePathUnfitted(width, height, seed, strokeWidth, options) {
	    const profile = getSeededStyleProfile(seed);
	    const edgeStyles = getSeededEdgeStyles(seed);
	    const minDimension = Math.min(width, height);
	    const baseAmplitude = clamp(
	      minDimension * 0.02 * profile.amplitudeScale,
	      SQUIGGLE_DEFAULTS.minAmplitude,
	      profile.maxAmplitude
	    );

	    const mode = options && options.mode ? options.mode : 'default';

	    // Default mode: keep a safe inner margin so stroke never clips at the SVG
	    // viewBox edges.
	    // Cutout mode (cookie-cutter): start edges at the element boundary and let
	    // fitPathToViewBox pull the path fully inside the viewBox. This avoids the
	    // visible "mat" (underfill) when --squiggle-inset is 0.
	    let margin;
	    if (mode === 'cutout') {
	      margin = 0;
	    } else {
	      // Margin must accommodate the *largest* possible edge amplitude for this seed.
	      // Using baseAmplitude can under-estimate (edge styles can push higher), which
	      // creates clip/stroke misalignment and visible "underflow" on frame variants.
	      const safeMargin = Math.max(strokeWidth + profile.maxAmplitude + 2, strokeWidth * 2);
	      margin = clamp(safeMargin, strokeWidth + 2, minDimension * 0.2);
	    }
	    const maxCornerRadius = Math.max(2, (minDimension - margin * 2) / 2);
	    // In cutout mode, start edges at the true box boundary (no fixed corner inset).
	    // The smoothing + wave shaping still yields rounded corners, but we avoid a
	    // visible "mat" (underfill) around the edges.
	    const cornerRadius =
	      mode === 'cutout'
	        ? 0
	        : clamp(
	            minDimension * 0.03 * profile.cornerScale,
	            SQUIGGLE_DEFAULTS.minCornerRadius,
	            Math.min(SQUIGGLE_DEFAULTS.maxCornerRadius * 1.8, maxCornerRadius)
	          );

    const left = margin;
    const top = margin;
    const right = width - margin;
    const bottom = height - margin;

    const topStart = { x: left + cornerRadius, y: top };
    const topEnd = { x: right - cornerRadius, y: top };
    const rightStart = { x: right, y: top + cornerRadius };
    const rightEnd = { x: right, y: bottom - cornerRadius };
    const bottomStart = { x: right - cornerRadius, y: bottom };
    const bottomEnd = { x: left + cornerRadius, y: bottom };
    const leftStart = { x: left, y: bottom - cornerRadius };
    const leftEnd = { x: left, y: top + cornerRadius };

    const edgeAmplitude = (length, edgeKey) =>
      clamp(
        baseAmplitude * Math.sqrt(length / minDimension) * (edgeStyles[edgeKey]?.amplitudeScale || 1),
        baseAmplitude * 0.4,
        profile.maxAmplitude
      );
    const edgeSegments = (length, edgeKey) =>
      clamp(
        Math.round((length / 60) * profile.segmentScale * (edgeStyles[edgeKey]?.segmentScale || 1)),
        6,
        34
      );

    const topLength = Math.max(1, topEnd.x - topStart.x);
    const rightLength = Math.max(1, rightEnd.y - rightStart.y);
    const bottomLength = Math.max(1, bottomStart.x - bottomEnd.x);
    const leftLength = Math.max(1, leftStart.y - leftEnd.y);

    const smoothFor = (edgeKey) =>
      Math.max(
        1,
        Math.round(profile.smoothingPasses + (edgeStyles[edgeKey]?.smoothingDelta || 0))
      );

    const points = [
      ...buildEdgePoints({
        start: topStart, end: topEnd, normal: { x: 0, y: -1 },
        segments: edgeSegments(topLength, 'top'), amplitude: edgeAmplitude(topLength, 'top'),
        seed: seed + 11, includeFirst: true,
        stepScale: profile.stepScale * (edgeStyles.top.stepScale || 1),
        smoothingPasses: smoothFor('top'),
        taperPower: edgeStyles.top.taperPower,
        waveStrength: edgeStyles.top.waveStrength,
        waveCycles: edgeStyles.top.waveCycles,
        wavePhase: edgeStyles.top.wavePhase,
        bowStrength: edgeStyles.top.bowStrength,
        asymmetry: edgeStyles.top.asymmetry
      }),
      ...buildEdgePoints({
        start: rightStart, end: rightEnd, normal: { x: 1, y: 0 },
        segments: edgeSegments(rightLength, 'right'), amplitude: edgeAmplitude(rightLength, 'right'),
        seed: seed + 37, includeFirst: false,
        stepScale: profile.stepScale * (edgeStyles.right.stepScale || 1),
        smoothingPasses: smoothFor('right'),
        taperPower: edgeStyles.right.taperPower,
        waveStrength: edgeStyles.right.waveStrength,
        waveCycles: edgeStyles.right.waveCycles,
        wavePhase: edgeStyles.right.wavePhase,
        bowStrength: edgeStyles.right.bowStrength,
        asymmetry: edgeStyles.right.asymmetry
      }),
      ...buildEdgePoints({
        start: bottomStart, end: bottomEnd, normal: { x: 0, y: 1 },
        segments: edgeSegments(bottomLength, 'bottom'), amplitude: edgeAmplitude(bottomLength, 'bottom'),
        seed: seed + 59, includeFirst: false,
        stepScale: profile.stepScale * (edgeStyles.bottom.stepScale || 1),
        smoothingPasses: smoothFor('bottom'),
        taperPower: edgeStyles.bottom.taperPower,
        waveStrength: edgeStyles.bottom.waveStrength,
        waveCycles: edgeStyles.bottom.waveCycles,
        wavePhase: edgeStyles.bottom.wavePhase,
        bowStrength: edgeStyles.bottom.bowStrength,
        asymmetry: edgeStyles.bottom.asymmetry
      }),
      ...buildEdgePoints({
        start: leftStart, end: leftEnd, normal: { x: -1, y: 0 },
        segments: edgeSegments(leftLength, 'left'), amplitude: edgeAmplitude(leftLength, 'left'),
        seed: seed + 83, includeFirst: false,
        stepScale: profile.stepScale * (edgeStyles.left.stepScale || 1),
        smoothingPasses: smoothFor('left'),
        taperPower: edgeStyles.left.taperPower,
        waveStrength: edgeStyles.left.waveStrength,
        waveCycles: edgeStyles.left.waveCycles,
        wavePhase: edgeStyles.left.wavePhase,
        bowStrength: edgeStyles.left.bowStrength,
        asymmetry: edgeStyles.left.asymmetry
      })
	    ];
	    return buildClosedPath(points);
	  }

	  function buildSquigglePath(width, height, seed, strokeWidth, options) {
	    const mode = options && options.mode ? options.mode : 'default';
	    const path = buildSquigglePathUnfitted(width, height, seed, strokeWidth, options);
	    if (!path) return '';
	    return fitPathToViewBox(path, width, height, strokeWidth, {
	      padExtra: mode === 'cutout' ? 0 : 1,
	      allowScaleUp: mode === 'cutout'
	    });
	  }

  function buildSquiggleSvg(options) {
    const { width, height, path, fill, stroke, strokeWidth, fillOpacity, strokeOpacity } = options;
    return (
      `<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 ${width} ${height}' preserveAspectRatio='none'>` +
      `<path d='${path}' fill='${fill}' fill-opacity='${fillOpacity}' stroke='${stroke}' stroke-opacity='${strokeOpacity}' stroke-width='${strokeWidth}' stroke-linecap='round' stroke-linejoin='round' vector-effect='non-scaling-stroke'/>` +
      `</svg>`
    );
  }

  // -------------------------------------------------------------------------
  // Application
  // -------------------------------------------------------------------------

  let colorProbe = null;
  function resolveCssColor(value, fallback) {
    const raw = (value || '').trim();
    if (!raw) return fallback;
    if (typeof document === 'undefined') return raw;

    if (!colorProbe) {
      colorProbe = document.createElement('span');
      colorProbe.style.position = 'absolute';
      colorProbe.style.left = '-9999px';
      colorProbe.style.top = '-9999px';
      colorProbe.style.width = '1px';
      colorProbe.style.height = '1px';
      colorProbe.style.pointerEvents = 'none';
      document.documentElement.appendChild(colorProbe);
    }

    colorProbe.style.color = raw;
    const computed = getComputedStyle(colorProbe).color;
    return (computed || fallback || raw).trim();
  }

  function parseComputedRgba(value, fallbackRgb) {
    const raw = String(value || '').trim();

    // rgb()/rgba() (commas or spaces, with optional "/ alpha")
    let match = raw.match(/rgba?\(([^)]+)\)/i);
    if (match) {
      const nums = (match[1].match(/[-\d.]+/g) || []).map((n) => parseFloat(n));
      const r = clamp(nums[0] ?? 0, 0, 255);
      const g = clamp(nums[1] ?? 0, 0, 255);
      const b = clamp(nums[2] ?? 0, 0, 255);
      const alpha = nums.length >= 4 ? clamp(nums[3], 0, 1) : 1;
      return { rgb: `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`, alpha };
    }

    // color(srgb r g b / a?) where r,g,b are 0..1 floats
    match = raw.match(/color\(srgb\s+([0-9.]+)\s+([0-9.]+)\s+([0-9.]+)(?:\s*\/\s*([0-9.]+))?\)/i);
    if (match) {
      const r = clamp(parseFloat(match[1]), 0, 1);
      const g = clamp(parseFloat(match[2]), 0, 1);
      const b = clamp(parseFloat(match[3]), 0, 1);
      const alpha = match[4] !== undefined ? clamp(parseFloat(match[4]), 0, 1) : 1;
      return { rgb: `rgb(${Math.round(r * 255)}, ${Math.round(g * 255)}, ${Math.round(b * 255)})`, alpha };
    }

    return { rgb: fallbackRgb || '#000000', alpha: 1 };
  }

  // -------------------------------------------------------------------------
  // Clip Path Management (for --frame variant)
  // -------------------------------------------------------------------------

  /**
   * Offset all x/y coordinates in an SVG path string by (dx, dy).
   * Works with our M/C/Z path format from buildClosedPath.
   */
  function offsetPathCoords(pathStr, dx, dy) {
    let idx = 0;
    return pathStr.replace(/-?[\d.]+/g, function(m) {
      var v = parseFloat(m);
      var offset = idx % 2 === 0 ? dx : dy;
      idx++;
      return (Math.round((v + offset) * 100) / 100).toString();
    });
  }

  /**
   * Build a clip path that exactly matches the stroke path.
   * Uses the same buildSquigglePath as the stroke SVG, then translates
   * coordinates from ::before space to element space.
   * This guarantees the clip aligns perfectly with the stroke.
   *
   * @param {number} width  - Element's layout width (pre-transform)
   * @param {number} height - Element's layout height (pre-transform)
   * @param {number} seed
   * @param {number} insetAbs - Absolute value of --squiggle-inset (e.g. 6, 8, 10)
   */
	  function buildClipPath(width, height, seed, insetAbs) {
	    var offset = Number.isFinite(insetAbs) ? insetAbs : 6;
	    var strokeW = width + offset * 2;
	    var strokeH = height + offset * 2;

	    // When offset is 0, use the unfitted path directly — fitPathToViewBox
	    // scales the path down which creates a visible cream "mat" gap at corners.
	    if (offset === 0) {
	      var rawPath = buildSquigglePathUnfitted(strokeW, strokeH, seed, SQUIGGLE_DEFAULTS.strokeWidth, { mode: 'cutout' });
	      return rawPath || '';
	    }

	    // Generate the exact same path used for the stroke SVG
	    var strokePath = buildSquigglePath(strokeW, strokeH, seed, SQUIGGLE_DEFAULTS.strokeWidth, {
	      mode: 'default'
	    });
	    if (!strokePath) return '';

    // Translate from ::before coordinate space to element coordinate space
    return offsetPathCoords(strokePath, -offset, -offset);
  }

  /**
   * Apply squiggle clip-path using CSS path() function.
   * This clips the content to the squiggle shape while keeping the stroke visible.
   *
   * @param {number} insetAbs - Absolute value of --squiggle-inset
   */
  function applySquiggleClip(element, seed, clipWidth, clipHeight, insetAbs) {
    const clipPath = buildClipPath(clipWidth, clipHeight, seed, insetAbs);
    if (!clipPath) return;

    const clipValue = `path('${clipPath}')`;

    // Apply clip to content children (not to frame itself, to preserve ::before stroke).
    // Priority: .media / .image-container wrappers, then direct media elements.
    let targets = element.querySelectorAll('.media, .image-container');
    if (!targets.length) {
      targets = element.querySelectorAll(':scope > img, :scope > svg, :scope > picture, :scope > video');
    }
    targets.forEach((child) => {
      child.style.clipPath = clipValue;
      child.style.webkitClipPath = clipValue;
    });
  }

  /**
   * Return the element's untransformed layout size.
   *
   * IMPORTANT: getBoundingClientRect() includes CSS transforms (e.g. rotations on
   * scrapbook cards), which breaks squiggle + clip-path alignment. We prefer
   * clientWidth/clientHeight (padding box) or offsetWidth/offsetHeight as a
   * transform-safe measure, with getBoundingClientRect() only as a fallback.
   */
  function getFrameLayoutSize(element) {
    if (!element) return { width: 0, height: 0 };

    // Layout size (untransformed). offset* includes borders; client* doesn't.
    // We'll prefer offset* as the "truth" for transform-safe dimensions.
    const baseWidth = element.offsetWidth || element.clientWidth || 0;
    const baseHeight = element.offsetHeight || element.clientHeight || 0;

    // getBoundingClientRect() gives us subpixel precision, but it can be
    // "inflated" by ancestor transforms (e.g. rotated scrapbook cards).
    // If rect size matches the untransformed base size, use it to avoid
    // tiny clip/stroke mismatches that show up as faint slivers.
    if (element.getBoundingClientRect) {
      const rect = element.getBoundingClientRect();
      const rectW = rect?.width || 0;
      const rectH = rect?.height || 0;

      if (rectW > 0 && rectH > 0) {
        if (baseWidth > 0 && baseHeight > 0) {
          const dw = Math.abs(rectW - baseWidth);
          const dh = Math.abs(rectH - baseHeight);

          // If the bounding box is noticeably larger, a rotation/scale is in play.
          // Stick to layout metrics in that case.
          if (dw <= 1.5 && dh <= 1.5) {
            return { width: rectW, height: rectH };
          }
        } else {
          // No usable layout metrics; use rect as last resort.
          return { width: rectW, height: rectH };
        }
      }
    }

    return { width: baseWidth, height: baseHeight };
  }

	  function applySquiggleFrame(element) {
	    if (!element || !element.getBoundingClientRect) return;
	    const layout = getFrameLayoutSize(element);
	    if (!layout.width || !layout.height) return;

	    const styles = getComputedStyle(element);
	    const inset = parsePixelValue(styles.getPropertyValue('--squiggle-inset'), 0);
	    const width = Math.max(1, layout.width - inset * 2);
	    const height = Math.max(1, layout.height - inset * 2);
	    const strokeWidth = SQUIGGLE_DEFAULTS.strokeWidth;

    const fillRaw = styles.getPropertyValue('--squiggle-fill-color') || '#f5f0eb';
    const strokeRaw = styles.getPropertyValue('--squiggle-stroke-color') || '#9c7d57';
    const fillComputed = resolveCssColor(fillRaw, '#f5f0eb');
    const strokeComputed = resolveCssColor(strokeRaw, '#9c7d57');
	    const fillParsed = parseComputedRgba(fillComputed, '#f5f0eb');
	    const strokeParsed = parseComputedRgba(strokeComputed, '#9c7d57');
	    const seed = getSquiggleSeed(element);
	    const isFrame = element.classList.contains('sc-squiggle-frame--frame');
	    const isCutout = isFrame && Math.abs(inset) < 0.5;
	    const path = buildSquigglePath(width, height, seed, strokeWidth, {
	      mode: isCutout ? 'cutout' : 'default'
	    });
	    if (!path) return;

    const svg = buildSquiggleSvg({
      width,
      height,
      path,
      fill: fillParsed.rgb,
      fillOpacity: fillParsed.alpha,
      stroke: strokeParsed.rgb,
      strokeOpacity: strokeParsed.alpha,
      strokeWidth
    });
    const encoded = encodeURIComponent(svg);
    element.style.setProperty('--squiggle-svg', `url("data:image/svg+xml,${encoded}")`);
    element.dataset.squiggleReady = 'true';

    // For --frame variant, also apply clip-path to content
    if (element.classList.contains('sc-squiggle-frame--frame')) {
      // Clip uses element's layout dimensions (pre-transform) and actual inset.
      const insetAbs = Math.abs(inset);
      applySquiggleClip(element, seed, layout.width, layout.height, insetAbs);
    }
  }

  // -------------------------------------------------------------------------
  // Observer & Initialization
  // -------------------------------------------------------------------------

  const observedFrames = new Set();
  const pendingFrames = new Set();
  let rafId = null;

  function scheduleUpdate(element) {
    pendingFrames.add(element);
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      pendingFrames.forEach((frame) => applySquiggleFrame(frame));
      pendingFrames.clear();
      rafId = null;
    });
  }

  const resizeObserver =
    typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver((entries) => {
          entries.forEach((entry) => scheduleUpdate(entry.target));
        })
      : null;

  function init(root) {
    const container = root || document;
    if (!container || !container.querySelectorAll) return;

    const applyFrame = (frame) => {
      applySquiggleFrame(frame);
      if (resizeObserver && !observedFrames.has(frame)) {
        resizeObserver.observe(frame);
        observedFrames.add(frame);
      }
    };

    if (container.nodeType === 1 && container.classList.contains('sc-squiggle-frame--full')) {
      applyFrame(container);
    }

    const frames = container.querySelectorAll('.sc-squiggle-frame--full');
    frames.forEach((frame) => {
      applyFrame(frame);
    });
  }

  // -------------------------------------------------------------------------
  // Export
  // -------------------------------------------------------------------------

  const api = {
    init: init,
    apply: applySquiggleFrame,
    buildPath: buildSquigglePath,
    buildPathUnfitted: buildSquigglePathUnfitted,
    DEFAULTS: SQUIGGLE_DEFAULTS
  };

  if (typeof window !== 'undefined') {
    window.SnowcubesSquiggle = api;

    // Auto-init on DOMContentLoaded
    if (typeof document !== 'undefined') {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => init());
      } else {
        init();
      }

    }
  }

  if (typeof module !== 'undefined') {
    module.exports = api;
  }
})();
